import { FC, useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { Box, Button, Theme } from '@mui/material'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'

import { ModalFormsContext } from 'components/modals'
import { themeVariables, useTheme } from 'themes'

const newFormThemeOverrides = {
    MuiButton: {
        styleOverrides: {
            outlined: {
                borderRadius: `${themeVariables.variables.buttonOutlined.borderRadius}`,
                minWidth: 120,
                height: 32,
                borderColor: `${themeVariables.color.border.main}`,
                borderWidth: 1,
                fontSize: 14,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover, &:active': {
                    backgroundColor: `${themeVariables.color.button.outlinedHover}`,
                    borderColor: `${themeVariables.color.border.active}`,
                },
            },
        },
    },
}

const styles = makeStyles<Theme>(theme => ({
    errorWrap: {
        [theme.breakpoints.up('md')]: {
            display: 'flex',
            alignItems: 'baseline',
        },
        '& a': {
            color: `${theme.color.primary.main}`,
            textDecoration: 'none',
        },
    },
    errorButton: {
        marginTop: 10,
        marginBottom: 10,

        [theme.breakpoints.down('sm')]: {
            marginTop: 10,
            marginLeft: 0,
        },
    },
    errorText: {
        marginRight: 5,
    },
}))

type TProps = {
    errorCode: number
    message: string
}

const AlertErrorCodes: FC<TProps> = ({ errorCode, message }) => {
    const style = styles()
    const { formTheme } = useTheme({ formTheme: newFormThemeOverrides })
    const { t, i18n } = useTranslation(['override', 'keys'])
    const navigate = useNavigate()
    const { modalValues } = useContext(ModalFormsContext)
    const errorKeyExist = i18n.exists(`keys:errors.${errorCode}`)

    const buttonHandler = path => {
        modalValues.handleClose()
        navigate(path)
    }

    const errorsList = {
        30001: (
            <Trans i18nKey="keys:errors.30001">
                <span className={style.errorText}>Deposit exceeds annual threshold.</span>
                <Button className={clsx(style.errorButton, 'button')} variant="outlined" onClick={() => buttonHandler('/settings/profile')}>
                    Increase limits.
                </Button>
            </Trans>
        ),
    }

    const customErrorTranslations = {
        10253: <Box dangerouslySetInnerHTML={{ __html: t('Authentication.AppleEmailError') }} />,
    }

    const customErrorTranslation = customErrorTranslations[errorCode]

    const renderErrorText = () => {
        if (errorKeyExist) {
            return errorsList[errorCode] ?? t(`keys:errors.${errorCode}`)
        }

        if (customErrorTranslation) {
            return customErrorTranslation
        }

        return message
    }

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={formTheme}>
                <div className="message">
                    <span className={style.errorWrap}>
                        <span data-sta-id={`warning-alert-span-${errorCode}`} className="text">
                            {renderErrorText()}
                        </span>
                    </span>
                </div>
            </ThemeProvider>
        </StyledEngineProvider>
    )
}

export default AlertErrorCodes
